import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import Button from 'screens/Integrations/components/IntegrationButton';
import 'styles/uiKitTableReset.css';
import 'screens/Integrations/components/integrationTable.css';
import PencilEditIcon from 'components/Icons/components/PencilEditIcon';
import useSubmissionUrlHandler from 'screens/Integrations/hooks/useSubmissionUrlHandler';

import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import ProductNameAndManufacture from './components/ProductNameAndManufacturer';
import { SubmissionSummaryContext } from '../../../../context/SubmissionSummaryProvider';

const fieldProductsTableDefaultPropProducts = [];
const fieldProductsTableDefaultPropErrors = [];
const FieldProductsTable = ({
  products = fieldProductsTableDefaultPropProducts,
  errors = fieldProductsTableDefaultPropErrors
}) => {
  const { events, triggerAnalyticsEvent } = useSubmissionAnalytics();
  const { redirectToTask } = useSubmissionUrlHandler();

  const {
    state: { submissionId, integrationType, isReadOnly }
  } = useContext(SubmissionSummaryContext);

  const [errorsMap, setErrorsMap] = useState({});

  const handleEditClick = product => {
    triggerAnalyticsEvent(events.epic.Integrations.submissionTaskClick, {
      vendor: integrationType
    });
    redirectToTask(product, submissionId, integrationType);
  };

  useEffect(() => {
    let map = {};
    errors.forEach(error => {
      const aux = error.taskIds.reduce((a, v) => ({ ...a, [v]: error }), {});
      map = { ...aux, ...map };
    });
    setErrorsMap(map);
  }, [errors]);

  const columns = [
    ...(errors
      ? [
          {
            key: 'error',
            render: (_, product) =>
              errorsMap[product.taskId] && (
                <span
                  className="bg-red-200 text-red-800 rounded-md text-xs p-2 font-semibold"
                  data-testid="span-error-message"
                >
                  {errorsMap[product.taskId].errorMessage}
                </span>
              )
          }
        ]
      : []),
    {
      key: 'date',
      title: 'Date',
      render: (_, product) => new Date(product.appliedDate).toLocaleDateString()
    },
    {
      key: 'product',
      title: 'Cropwise Product',
      render: (_, product) => <ProductNameAndManufacture product={product} />
    },
    { key: 'productType', title: 'Product Type', dataIndex: 'productType' },
    {
      key: 'applicationMethod',
      title: 'Application Method',
      dataIndex: 'applicationMethod'
    },
    {
      key: 'rate',
      title: 'Rate (per acre)',
      render: (_, product) =>
        `${product.ratePerArea.toFixed(3)} ${product.ratePerAreaUnit}`
    },
    {
      key: 'quantity',
      title: 'Total Applied',
      render: (_, product) =>
        `${product.quantity.toFixed(3)} ${product.quantityUnit}`
    },
    ...(!isReadOnly
      ? [
          {
            key: 'actions',
            render: (_, product) => (
              <Button
                variant="text"
                icon={<PencilEditIcon />}
                onClick={() => handleEditClick(product)}
              >
                Edit
              </Button>
            )
          }
        ]
      : [])
  ];

  return (
    <div className="cwf-table-flush cwf-table-static-header cwf-table-no-row-hover integration-table">
      <Table
        rowKey={product => `${product.taskId}:${product.productId}`}
        columns={columns}
        dataSource={products}
        pagination={false}
        locale={{ emptyText: null }}
        rowClassName={product =>
          errors && errorsMap[product.taskId] ? 'bg-red-100' : ''
        }
      />
    </div>
  );
};

FieldProductsTable.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  errors: PropTypes.arrayOf(PropTypes.object)
};

export default FieldProductsTable;
